/**
 * What a file attached to a record may be.
 *
 * Here rather than in the API because both ends need the same answer. The limit
 * and the allowlist are enforced on the server — a browser's check is a
 * courtesy, not a control — but a page that does not know them can only offer
 * the user every file on their machine and report the refusal afterwards, once
 * the bytes are already going up.
 */

/** How long a presigned URL is good for. Long enough to upload, short enough not to be a key. */
export const URL_TTL_SECONDS = 120;

/**
 * How long a download link written into an export is good for — one day.
 *
 * Longer than {@link URL_TTL_SECONDS} because a sheet is opened later than a
 * page is clicked; short because the link needs no sign-in, so anyone the file
 * is forwarded to can open the document until it lapses.
 */
export const EXPORT_URL_TTL_SECONDS = 86400;

/**
 * The largest file accepted.
 *
 * Not a technical limit — S3 would take far more — but a document that runs to
 * tens of megabytes is a scan nobody compressed, and storing it costs the
 * business money every month forever.
 */
export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;

/**
 * Types accepted.
 *
 * An allowlist rather than a blocklist. These files are downloaded by
 * colleagues, and the set of things worth attaching to a record is small and
 * dull: documents, images of documents, and nothing executable.
 */
export const ALLOWED_CONTENT_TYPES: readonly string[] = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/tiff",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
];

/** The `accept` attribute for a file input, from the allowlist above. */
export const DOCUMENT_ACCEPT_ATTRIBUTE = ALLOWED_CONTENT_TYPES.join(",");

/**
 * What an AFR may carry: PDF, Word and Excel, nothing else.
 *
 * Narrower than {@link ALLOWED_CONTENT_TYPES} because every one of these can be
 * read for the company's name before it is attached. A photo of a return or a
 * text file would be the one document in the year that skipped the check, and
 * the check is the point of attaching it here rather than emailing it round.
 *
 * Told apart by extension, not by the browser's `File.type`: that comes from
 * the operating system's registry and is empty, or `application/octet-stream`,
 * for an `.xlsx` on a machine without Office. The content type sent up is the
 * one on this table, so the presigned PUT and the stored row always agree.
 */
export const AFR_DOCUMENT_TYPES = [
  { extension: ".pdf", format: "pdf", contentType: "application/pdf" },
  { extension: ".doc", format: "doc", contentType: "application/msword" },
  {
    extension: ".docx",
    format: "docx",
    contentType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  { extension: ".xls", format: "xls", contentType: "application/vnd.ms-excel" },
  {
    extension: ".xlsx",
    format: "xlsx",
    contentType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  },
] as const;

export type AfrDocumentType = (typeof AFR_DOCUMENT_TYPES)[number];

/** The AFR type a file name is, or null when it is not one an AFR takes. */
export function afrDocumentType(fileName: string): AfrDocumentType | null {
  const name = fileName.trim().toLowerCase();
  return AFR_DOCUMENT_TYPES.find((type) => name.endsWith(type.extension)) ?? null;
}

/**
 * The `accept` attribute for an AFR file input.
 *
 * Extensions as well as types, so the picker still offers an `.xlsx` whose type
 * the operating system does not know.
 */
export const AFR_DOCUMENT_ACCEPT_ATTRIBUTE = AFR_DOCUMENT_TYPES.flatMap((type) => [
  type.extension,
  type.contentType,
]).join(",");

/** How the allowed types are named to a person. */
export const AFR_DOCUMENT_TYPES_LABEL = "PDF, Word (.doc, .docx) and Excel (.xls, .xlsx)";
